import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminService from '@/services/adminService';
import type {
  AdminOverviewData,
  DailyActivityPoint,
  FarmAnalyticsData,
  UserSearchResult,
  UserSearchParams,
  UserDetailData,
  BulkStatusRequest,
} from '@/types/admin';

interface AdminState {
  overview: AdminOverviewData | null;
  overviewLoading: boolean;
  overviewError: string | null;

  userActivity: DailyActivityPoint[];
  userActivityLoading: boolean;

  farmAnalytics: FarmAnalyticsData | null;
  farmAnalyticsLoading: boolean;

  userSearchResults: UserSearchResult[];
  userSearchTotal: number;
  userSearchParams: UserSearchParams;
  userSearchLoading: boolean;

  selectedUserDetail: UserDetailData | null;
  userDetailLoading: boolean;

  bulkStatusLoading: boolean;
}

const initialState: AdminState = {
  overview: null,
  overviewLoading: false,
  overviewError: null,

  userActivity: [],
  userActivityLoading: false,

  farmAnalytics: null,
  farmAnalyticsLoading: false,

  userSearchResults: [],
  userSearchTotal: 0,
  userSearchParams: { sort_by: 'created_at', sort_dir: 'desc', skip: 0, limit: 20 },
  userSearchLoading: false,

  selectedUserDetail: null,
  userDetailLoading: false,

  bulkStatusLoading: false,
};

export const fetchOverview = createAsyncThunk('admin/fetchOverview', async () => {
  return await adminService.getOverview();
});

export const fetchUserActivity = createAsyncThunk(
  'admin/fetchUserActivity',
  async (periodDays: number = 30) => {
    return await adminService.getUserActivity(periodDays);
  }
);

export const fetchFarmAnalytics = createAsyncThunk(
  'admin/fetchFarmAnalytics',
  async (locale: string = 'en') => {
    return await adminService.getFarmAnalytics(locale);
  }
);

export const searchUsers = createAsyncThunk(
  'admin/searchUsers',
  async (params: UserSearchParams) => {
    return await adminService.searchUsers(params);
  }
);

export const fetchUserDetail = createAsyncThunk(
  'admin/fetchUserDetail',
  async (userId: number) => {
    return await adminService.getUserDetail(userId);
  }
);

export const bulkUpdateStatus = createAsyncThunk(
  'admin/bulkUpdateStatus',
  async (data: BulkStatusRequest) => {
    return await adminService.bulkUpdateStatus(data);
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setUserSearchParams(state, action) {
      state.userSearchParams = { ...state.userSearchParams, ...action.payload };
    },
    clearUserDetail(state) {
      state.selectedUserDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Overview
      .addCase(fetchOverview.pending, (state) => { state.overviewLoading = true; state.overviewError = null; })
      .addCase(fetchOverview.fulfilled, (state, action) => { state.overviewLoading = false; state.overview = action.payload; })
      .addCase(fetchOverview.rejected, (state, action) => { state.overviewLoading = false; state.overviewError = action.error.message || 'Failed to load'; })
      // User Activity
      .addCase(fetchUserActivity.pending, (state) => { state.userActivityLoading = true; })
      .addCase(fetchUserActivity.fulfilled, (state, action) => { state.userActivityLoading = false; state.userActivity = action.payload; })
      .addCase(fetchUserActivity.rejected, (state) => { state.userActivityLoading = false; })
      // Farm Analytics
      .addCase(fetchFarmAnalytics.pending, (state) => { state.farmAnalyticsLoading = true; })
      .addCase(fetchFarmAnalytics.fulfilled, (state, action) => { state.farmAnalyticsLoading = false; state.farmAnalytics = action.payload; })
      .addCase(fetchFarmAnalytics.rejected, (state) => { state.farmAnalyticsLoading = false; })
      // Search Users
      .addCase(searchUsers.pending, (state) => { state.userSearchLoading = true; })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.userSearchLoading = false;
        state.userSearchResults = action.payload.users;
        state.userSearchTotal = action.payload.total;
      })
      .addCase(searchUsers.rejected, (state) => { state.userSearchLoading = false; })
      // User Detail
      .addCase(fetchUserDetail.pending, (state) => { state.userDetailLoading = true; })
      .addCase(fetchUserDetail.fulfilled, (state, action) => { state.userDetailLoading = false; state.selectedUserDetail = action.payload; })
      .addCase(fetchUserDetail.rejected, (state) => { state.userDetailLoading = false; })
      // Bulk Status
      .addCase(bulkUpdateStatus.pending, (state) => { state.bulkStatusLoading = true; })
      .addCase(bulkUpdateStatus.fulfilled, (state) => { state.bulkStatusLoading = false; })
      .addCase(bulkUpdateStatus.rejected, (state) => { state.bulkStatusLoading = false; });
  },
});

export const { setUserSearchParams, clearUserDetail } = adminSlice.actions;
export const adminReducer = adminSlice.reducer;
